// insight รายวันของผู้ใช้ + การโต้ตอบ (ใช้ร่วมกันทุกช่องทาง: แถบหน้าแรก, 💡, ป๊อปอัป, หน้าประวัติ)
import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Swal from 'sweetalert2';
import api from '../../adapters/api';
import { downloadAttachment } from './InsightCard';
import { useInsightMe } from './useInsightInline';
import { showInsightUndo } from './InsightUndoSnackbar';

const STATUS_DONE_TEXT = { done: 'บันทึกว่า "ทำแล้ว"', skipped: 'ข้ามเรื่องนี้แล้ว', open: 'ย้อนสถานะแล้ว' };

// ส่งคำขอของ task เดียวกันตามลำดับ (กดแล้วกด "ยกเลิก" ทันที ต้องไม่สลับลำดับที่ server)
const chains = new Map();
function inOrder(taskId, request) {
  const next = (chains.get(taskId) || Promise.resolve()).catch(() => {}).then(request);
  chains.set(taskId, next);
  next.finally(() => { if (chains.get(taskId) === next) chains.delete(taskId); }).catch(() => {});
  return next;
}

export function logInsightEvent(taskId, event) {
  return api.post(`/api/insight/tasks/${taskId}/event`, { event }).catch(() => { /* สัญญาณแฝง — พลาดได้ */ });
}

// date ไม่ระบุ = วันนี้ (ใช้ cache ร่วมกันทุกช่องทาง) · ระบุ = วันก่อนหน้า (หน้าประวัติ)
export default function useInsightTasks(enabled, date = null) {
  const qc = useQueryClient();
  const { data: me } = useInsightMe();
  const readOnly = Boolean(me?.viewAs); // โหมดดูในฐานะผู้ใช้: ดู/เปิดรายการ/ดาวน์โหลดได้ แต่บันทึกอะไรไม่ได้
  const KEY = date ? ['insightTasks', date] : ['insightTasks'];
  const query = useQuery({
    queryKey: KEY,
    queryFn: async () => (await api.get('/api/insight/tasks', { params: date ? { date } : {} })).data,
    enabled,
    refetchInterval: 15 * 60 * 1000,
  });

  // อัปเดตหน้าจอทันที แล้วค่อยยืนยันกับ server (ล้มเหลว = โหลดใหม่ + แจ้ง)
  const mutate = useCallback(async (taskId, patch, request) => {
    qc.setQueryData(KEY, old => old && { ...old, tasks: old.tasks.map(t => (t.id === taskId ? { ...t, ...patch } : t)) });
    try {
      await inOrder(taskId, request);
    } catch (err) {
      qc.invalidateQueries({ queryKey: ['insightTasks'] });
      Swal.fire({
        icon: 'error', title: 'บันทึกไม่สำเร็จ', text: err.response?.data?.error || err.message,
        confirmButtonColor: '#1976d2', confirmButtonText: 'ตกลง',
      });
    }
  }, [qc, date]); // eslint-disable-line react-hooks/exhaustive-deps

  const actionsFor = useCallback((task, { onOpenRows } = {}) => (readOnly ? {
    preview: true,
    openRows: onOpenRows ? () => onOpenRows(task) : undefined,
    download: () => downloadAttachment(`/api/insight/tasks/${task.id}/attachment`),
  } : {
    feedback: (value, reason) => mutate(task.id, { feedback: value || null, feedbackReason: value ? reason : null },
      () => api.post(`/api/insight/tasks/${task.id}/feedback`, { value, reason })),
    // บันทึกทันที แล้วให้กด "ยกเลิก" ได้ราว 6 วิ (กันกดผิด) — ยกเลิก = ส่งสถานะ/เหตุผล/หมายเหตุเดิมกลับไป
    status: (status, extra = {}) => {
      const setTo = (st, ex) => mutate(task.id, { status: st, skipReason: ex.skipReason || null, note: ex.note || null },
        () => api.post(`/api/insight/tasks/${task.id}/status`, { status: st, ...ex }));
      const prev = { status: task.status || 'open', skipReason: task.skipReason || undefined, note: task.note || undefined };
      setTo(status, extra);
      showInsightUndo({
        message: STATUS_DONE_TEXT[status] || 'บันทึกแล้ว',
        onUndo: () => setTo(prev.status, prev.status === 'skipped' ? { skipReason: prev.skipReason, note: prev.note } : {}),
      });
    },
    openRows: onOpenRows ? () => { logInsightEvent(task.id, 'drawer_open'); onOpenRows(task); } : undefined,
    download: () => downloadAttachment(`/api/insight/tasks/${task.id}/attachment`),
  }), [mutate, readOnly]);

  return { ...query, actionsFor, readOnly };
}
